import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import { 
  Building2, 
  Globe, 
  AlertTriangle, 
  TrendingUp,
  BarChart3,
  GitCompare,
  MessageSquareText,
  FileText,
  ArrowRight,
  Zap
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const api = useApi();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/api/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Platforms",
      value: stats?.platforms_analyzed || 0,
      subtitle: "Analyzed",
      icon: Building2,
      color: "purple"
    },
    {
      title: "Markets",
      value: stats?.markets_covered || 0,
      subtitle: "EU + UK",
      icon: Globe,
      color: "blue"
    },
    {
      title: "Gaps",
      value: stats?.compliance_gaps_identified || 0,
      subtitle: "Identified",
      icon: AlertTriangle,
      color: "orange"
    },
    {
      title: "Opportunities",
      value: stats?.service_opportunities || 0,
      subtitle: "Full outsource",
      icon: TrendingUp,
      color: "green"
    }
  ];

  const featureCards = [
    {
      title: "Gap Analysis",
      description: "Identify in-house vs. outsource needs",
      icon: BarChart3,
      path: "/gap-analysis"
    },
    {
      title: "Comparison",
      description: "Side-by-side platform requirements",
      icon: GitCompare,
      path: "/comparison"
    },
    {
      title: "Pitch Generator",
      description: "Generate targeted sales pitches",
      icon: MessageSquareText,
      path: "/pitch-generator"
    },
    {
      title: "Reports",
      description: "Detailed compliance reports",
      icon: FileText,
      path: "/reports"
    }
  ];

  if (loading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper" data-testid="dashboard-page">
      <div className="page-header compact">
        <h1 className="page-title">EPR/PPR Compliance Hub</h1>
        <p className="page-subtitle">
          Strategic analysis for compliance services
        </p>
      </div>

      {/* PPWR Deadline Banner */}
      <div className="deadline-banner animate-fade-in" data-testid="deadline-banner">
        <div className="deadline-content">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-yellow-300" />
            <h3>EU PPWR 2025 Enforcement</h3>
          </div>
          <p>Core obligations effective Aug 12, 2026</p>
        </div>
        <div className="deadline-days">
          <div className="deadline-number" data-testid="deadline-days">
            {stats?.ppwr_deadline_days || 0}
          </div>
          <div className="deadline-label">days remaining</div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        {statCards.map((card, index) => (
          <div 
            key={card.title} 
            className={`stat-card animate-fade-in`}
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`stat-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="stat-card-header">
              <span className="stat-card-title">{card.title}</span>
              <div className={`stat-card-icon ${card.color}`}>
                <card.icon size={14} />
              </div>
            </div>
            <div className="stat-card-value">{card.value}</div>
            <div className="stat-card-subtitle">{card.subtitle}</div>
          </div>
        ))}
        {/* Markets Badge Card */}
        <div className="stat-card animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="stat-card-header">
            <span className="stat-card-title">Key Markets</span>
            <div className="stat-card-icon blue">
              <Globe size={14} />
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {stats?.key_markets?.slice(0, 6).map((market) => (
              <span 
                key={market}
                className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded border border-gray-200"
              >
                {market}
              </span>
            ))}
            {(stats?.key_markets?.length || 0) > 6 && (
              <span className="text-[10px] text-gray-400">+{stats.key_markets.length - 6}</span>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Quick Actions</h2>
      <div className="grid grid-cols-4 gap-3 mb-4">
        {featureCards.map((card, index) => (
          <div 
            key={card.title}
            className={`feature-card animate-fade-in`}
            style={{ animationDelay: `${index * 0.1}s` }}
            onClick={() => navigate(card.path)}
            data-testid={`feature-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="feature-card-icon">
              <card.icon size={18} />
            </div>
            <h3 className="feature-card-title">{card.title}</h3>
            <p className="feature-card-description line-clamp-2">{card.description}</p>
            <div className="flex items-center gap-1 text-violet-600 text-[10px] font-medium mt-2">
              <span>Get started</span>
              <ArrowRight size={10} />
            </div>
          </div>
        ))}
      </div>

      {/* Platform Overview */}
      <div className="card p-4 animate-fade-in" style={{ animationDelay: '0.5s' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            Platform Overview
          </h3>
          <button 
            onClick={() => navigate('/platforms')}
            className="text-violet-600 text-xs font-medium flex items-center gap-1 hover:gap-1.5 transition-all"
          >
            View all <ArrowRight size={10} />
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between py-3 border-b border-gray-100 hover:bg-gray-50 rounded-lg px-2 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-sm font-bold text-orange-600">A</span>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Amazon</div>
                <div className="text-xs text-gray-500">Comprehensive EPR via SP-API</div>
              </div>
            </div>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded-full">
              API Available
            </span>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-100 hover:bg-gray-50 rounded-lg px-2 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-sm font-bold text-orange-600">T</span>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Temu</div>
                <div className="text-xs text-gray-500">High service opportunity</div>
              </div>
            </div>
            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-medium rounded-full">
              Full Outsource
            </span>
          </div>
          <div className="flex items-center justify-between py-3 hover:bg-gray-50 rounded-lg px-2 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-sm font-bold text-blue-600">G</span>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">GPAIS (EU Registry)</div>
                <div className="text-xs text-gray-500">Model for EU-wide implementation</div>
              </div>
            </div>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded-full">
              Public Registry
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
